import { fireErrorAnalytics } from '@atlassian/error-handling';
import { getExperimentValue, getTargetingGate } from '../../feature-gates';
import { OnboardingTemplate } from './template';
import { IGNORE_MAU_HEADER, EP_ENITITY_ID_HEADER } from '../../../services/create-confluence-draft';
import { fetchJson } from '../../../services/utils';
interface PageResponse {
	body: {
		atlas_doc_format: {
			value: string;
			representation: string;
		};
	};
}

interface UpdateExistingPageWithInlineOnboardingTemplateParams {
	pageId: string;
	template: OnboardingTemplate;
	projectId?: number;
	projectType?: string;
	pageTitle?: string;
}

export const PROJECT_PAGES_XFLOW_INLINE_ONBOARDING = 'PROJECT_PAGES_XFLOW_INLINE_ONBOARDING';

// https://console.statsig.com/LqivKg6ADZZaGczRfBKfX/experiments/grw-1519_jira_project_pages_inline_onboarding
export const INLINE_ONBOARDING_AB_EXPERIMENT_KEY = 'grw-1519_jira_project_pages_inline_onboarding';

// https://console.statsig.com/LqivKg6ADZZaGczRfBKfX/gates/grw-1519_jira_project_pages_inline_onboarding_gate
export const INLINE_ONBOARDING_KILL_SWITCH = 'grw-1519_jira_project_pages_inline_onboarding_gate';

export const flagUserAsXflowedFromProjectPages = () => {
	try {
		getTargetingGate(INLINE_ONBOARDING_KILL_SWITCH) &&
			window.localStorage.setItem(PROJECT_PAGES_XFLOW_INLINE_ONBOARDING, 'true');
	} catch (error) {
		fireErrorAnalytics({
			error: new Error(`Error setting inline onboarding flag`),
			meta: {
				id: 'project-pages-set-inline-onboarding-flag-error',
				packageName: '@atlassiansox/project-pages',
				teamName: 'starburst',
			},
		});
	}
};

export const getInlineOnboardingABCohort = () => {
	try {
		if (
			getTargetingGate(INLINE_ONBOARDING_KILL_SWITCH) &&
			window.localStorage.getItem(PROJECT_PAGES_XFLOW_INLINE_ONBOARDING) === 'true'
		) {
			window.localStorage.removeItem(PROJECT_PAGES_XFLOW_INLINE_ONBOARDING);
			return getExperimentValue(INLINE_ONBOARDING_AB_EXPERIMENT_KEY, 'not-enrolled', true);
		}
	} catch (error) {
		fireErrorAnalytics({
			error: new Error(`Error checking inline onboarding cohort`),
			meta: {
				id: 'project-pages-check-inline-onboarding-cohort-error',
				packageName: '@atlassiansox/project-pages',
				teamName: 'starburst',
			},
		});
	}
};

export const updateExistingPageWithInlineOnboardingTemplate = async ({
	pageId,
	template,
	projectId,
	projectType,
	pageTitle,
}: UpdateExistingPageWithInlineOnboardingTemplateParams) => {
	try {
		// First get the existing content
		const existingPage = await fetchJson<PageResponse>(
			`/wiki/api/v2/pages/${pageId}?body-format=atlas_doc_format`,
			{
				method: 'GET',
				credentials: 'include',
				headers: {
					'Content-Type': 'application/json',
					[IGNORE_MAU_HEADER]: 'true',
					[EP_ENITITY_ID_HEADER]: JSON.stringify({
						projectId,
						projectType,
					}),
				},
			},
		);

		// Put initial content first, then the blueprint content
		const originalContent = JSON.parse(existingPage.body.atlas_doc_format.value);
		const combinedContent = {
			...originalContent,
			content: [...(template?.atlas_doc_format.value.content || []), ...originalContent.content],
		};

		const updateRequestBody = {
			id: pageId,
			status: 'draft',
			title: pageTitle,
			body: {
				value: JSON.stringify(combinedContent),
				representation: 'atlas_doc_format',
			},
			version: {
				number: 1,
				message: 'Initial content update',
			},
		};

		await fetchJson(`/wiki/api/v2/pages/${pageId}`, {
			method: 'PUT',
			credentials: 'include',
			headers: {
				'Content-Type': 'application/json',
				[IGNORE_MAU_HEADER]: 'true',
				[EP_ENITITY_ID_HEADER]: JSON.stringify({
					projectId,
					projectType,
				}),
			},
			body: JSON.stringify(updateRequestBody),
		});
	} catch (error) {
		fireErrorAnalytics({
			error: new Error('Failed to update page with inline onboarding template'),
			meta: {
				id: 'project-pages-update-inline-onboarding-template-error',
				packageName: '@atlassiansox/project-pages',
				teamName: 'starburst',
			},
		});
	}
};
